import { Screenshot, SimpleFeature } from '../../../api/core'
import { Analyst } from '../../../types/Analyst'
import { TranslationMap } from '../../../types/TranslationMap'
import { VersionInfo } from '../../../types/VersionInfo'
import { VersionTag } from '../../../types/VersionTag'
import type { Game } from '../../game'
import { GameReview } from '../../game/types/GameReview'
import { GameStageId } from '../../game/types/GameStageId'
import type { Duration, EventComment } from '../../live-events/types/TrackingEvents'

type BaseType = 'live_event' | 'singlegame' | (string & {})

export interface UnifiedNewsEntriesNewsData {
  lastEvents?: boolean
  newsData: UnifiedNewsEntry[]
}
export interface UnifiedNewsEntry {
  feedEntry: NewsEntry
  comment?: any
  history?: any
  dhistory?: any
  hasFTUEVideo: number
  revenueDownloadsData?: {
    percentageIncreaseRevenue: number
    percentageIncreaseDownload: number
    sum14BeforeRevenue: number
    sum14BeforeDownloads: number
    sum14AfterRevenue: number
    sum14AfterDownloads: number
    history?: {
      [timestamp: string]: {
        country: string
        revenue: number
        downloads: number
      }
    }
  }
  review?: GameReview
  versionInfo?: VersionInfo
  downloadable?: boolean // reports only - indicates if user has download access
}

export interface NewsEntryFeature {
  featureName: string
  featureName_translation: string
  choice2LegacyId: number
  featureLegacyId: number
  choice1Id: string
  choice1LegacyId: number
  featureId: string
  choice1: string
  choice2: string
  choice2Id: string
  choice1_translation: string
  choice2_translation: string
}

export interface NewsEntry {
  id: string
  publishedAt: number
  type: NewsEntryType
  title?: string
  titles: TranslationMap
  subtitle?: string
  subtitles: TranslationMap
  entryData: {
    appId: number
    icon: string
    game: string
    gameId: string
    gameVersion: string
    artist: string
    conventionalSubgenre?: string
    conventionalSubgenreId?: string
    rank: number
    versionTags?: Array<VersionTag>
    organizationId?: string
    image: {
      id: string
      url: string
      mimeType: string
    }
    content: TranslationMap
    action: {
      label: TranslationMap
      type: string
      target: string
    }

    versionReleaseDate?: number
    features?: Array<NewsEntryFeature>
    screenshots?: Array<NewsEntryScreenshot>
    softlaunchMarkets?: Array<string>
    similarGames?: Array<NewsEntrySimilarGame>
    stageId?: GameStageId
    gps?: number
    gpsBefore?: number
    gpsChange?: number
    commentPublished?: boolean
    commentId?: string
    typeId: string
    eventId?: string
    analyst?: Analyst
    comment?: EventComment | TranslationMap
    durations?: Duration[]
    start?: number
    end?: number
    screenshotUrls?: string[]
    sticky?: boolean
    version?: string
    coverUrls?: string[] // reports only
    title?: TranslationMap // reports only
    tags?: string[] // reports only
    analysts?: Analyst[] // reports only
    games?: Game[] // reports only
    reportId?: string // reports only
  }
  gameId: string
  market?: string
  baseType?: BaseType
}

export interface NewsEntryScreenshot extends Omit<Screenshot, 'features'> {
  features?: NewsEntryScreenshotFeature[]
}

export interface NewsEntryScreenshotFeature extends SimpleFeature {
  featureName_translation: string
  choiceName_translation: string
}

export interface NewsEntrySimilarGame {
  gameId: string
  distance: number
  artist: string
  appId: number
  name: string
  icon: null
  icons: string[]
}

export enum NewsEntryType {
  EntryTop10 = 'entrytop10',
  EntryTop20 = 'entrytop20',
  EntryTop50 = 'entrytop50',
  EntryTop100 = 'entrytop100',
  EntryTop200 = 'entrytop200',
  Editorial = 'editorial',
  GpsNew = 'gpsnew',
  GpsOutside = 'gpsoutside',
  GpsUpdate = 'gpsupdate',
  GpsSystem = 'gpssystem',
  MajorGameRelease = 'majorgamerelease',
  PerformanceChangeRevenue = 'performancechangerevenue',
  PerformanceChangeDownload = 'performancechangedownload',
  SoftLaunch = 'softlaunch',
  SoftLaunchNonAnalyzed = 'softlaunchnonanalyzed',
  LiveEventNewEventType = 'live_event_new_event_type',
  LiveEventAnalystNotes = 'live_event_analyst_notes',
  Report = 'report',
  LiveEventTrackedGame = 'live_event_tracked_game',
}
